import * as React from 'react';

import { ErrorBoundary } from 'bernie-view';

import { DeviceInformation } from '@shared-ui/viewport-context';
import { IntersectionTracker, useClickTracker } from '@shared-ui/clickstream-analytics-context';
import { useLocalization } from '@shared-ui/localization-context';

import { adaptShortList } from 'stores/adapters/bex-api/migration-adapters/common/shortlist-adapter';
import { getGalleryModel } from 'stores/adapters';
import { usePropertyListing } from 'stores/contexts';

import { PackageType } from 'models/multi-item/multi-item-types';
import { RfrrPageNamespace } from 'src/common/constants/analytics';

import { UitkButtonTheme } from 'uitk-react-button';
import { UitkFigure, UitkFigureAspectRatioType, UitkImage, UitkQueuedImage } from 'uitk-react-images';
import { UitkGalleryCarousel } from 'uitk-react-gallery-carousel';
import { UitkLayoutFlexItem } from 'uitk-react-layout-flex';

import { imageGalleryAlgorithmTrackingSchema, productsTrackingSchema } from 'utils/uis-prime-micro-messages';
import { updateSlotsParamForMapTravelAds } from 'utils/infosite-url-utils';

import { determineTarget, TargetableLink } from 'src/common/components/common/targetable-link';
import { DoubleLink } from 'src/common/components/common/media/double-link';
import { FavoriteButton } from 'components/common/shortlist/favorite-button';
import { PropertyFields, Theme } from '__generated__/api';

export interface ListingGalleryProps {
  deviceInformation: DeviceInformation;
  listingClickHandler: (property: PropertyFields.Fragment) => void;
  navigateOnClient: (relativePath: string) => void;
  mapListingView?: boolean;
  onFavoriteClick?: () => void;
  packageType: PackageType | undefined;
  ratio: UitkFigureAspectRatioType;
}

export interface ListingGalleryState {
  currentGalleryIndex: number;
}

export const ListingGallery: React.FC<ListingGalleryProps> = props => {
  const l10n = useLocalization();
  const property = usePropertyListing();
  const track = useClickTracker();
  const [currentGalleryIndex, setCurrentGalleryIndex] = React.useState(0);

  const { deviceInformation, listingClickHandler, mapListingView, navigateOnClient, onFavoriteClick, packageType, ratio } = props;

  const {
    id,
    name,
    offerSummary,
    pinnedDetails,
    propertyDetailsLink,
    propertyGallery,
    propertyImage,
    shortList,
    sponsoredListing,
  } = property;

  const brandedDealType = offerSummary?.messages?.find(message => message.theme === Theme.BRANDED_DEAL)?.type;
  const hotelId = parseInt(id, 10);
  const pinned = Boolean(pinnedDetails?.heading);

  /*
   * To be followed up soon on surfacing analytics data from Bex-Api
   * https://jira.expedia.biz/browse/LSHOP-256
   */
  const referrerId = `MainCarousel.property-listing-image-carousel${sponsoredListing ? '-sponsored' : ''}.GalleryCarousel`;
  const uisPrimeMicroMessages = galleryIndex => [
    imageGalleryAlgorithmTrackingSchema(
      hotelId,
      parseInt(propertyGallery[galleryIndex]?.imageId, 10) || undefined,
      'heroaesthetics.v20190411-hsc.v20190223'
    ),
  ];

  const commonClickFns = () => {
    if (listingClickHandler) {
      listingClickHandler(property);
    }

    track(propertyDetailsLink?.clientSideAnalytics.referrerId, propertyDetailsLink?.clientSideAnalytics.linkName, true, [
      ...productsTrackingSchema(parseInt(id, 10), pinned, Boolean(sponsoredListing), packageType, brandedDealType),
    ]);
    track(
      `HOT.SR:PHOTOS.${hotelId}.${propertyGallery[currentGalleryIndex]?.subjectId}`,
      'Image clicked on HSR image gallery',
      true,
      uisPrimeMicroMessages(currentGalleryIndex)
    );
  };

  const getLinkDestination = () => {
    let to = sponsoredListing && packageType ? propertyDetailsLink?.uri.value : propertyDetailsLink?.uri.relativePath;
    if (sponsoredListing && mapListingView) {
      to = updateSlotsParamForMapTravelAds(to);
    }

    return to;
  };

  const navigateToDestination = () => {
    commonClickFns();

    const target = determineTarget(deviceInformation, packageType !== null);

    if (target) {
      window.open(getLinkDestination(), target);
    } else {
      navigateOnClient(getLinkDestination());
    }
  };

  if (!propertyGallery || propertyGallery.length === 0) {
    if (propertyImage) {
      const doubleLinkProps = {
        to: getLinkDestination(),
        onClick: commonClickFns,
      };

      return (
        <TargetableLink>
          <DoubleLink {...doubleLinkProps}>
            <UitkFigure>
              <UitkImage alt={propertyImage.image.description} placeholderImage={false} src={propertyImage.image.url} />
            </UitkFigure>
          </DoubleLink>
        </TargetableLink>
      );
    }

    return (
      <UitkFigure>
        <UitkImage alt="" placeholderImage={false} />
      </UitkFigure>
    );
  }

  const updateIndex = (index: number) => {
    if (index >= 0) {
      const clickDirection = currentGalleryIndex - index === -1 || currentGalleryIndex - index > 1 ? 'clickRight' : 'clickLeft';
      track(
        `${RfrrPageNamespace.Search}.${referrerId}.${clickDirection}.${index}`,
        'Change image in listing carousel',
        null,
        uisPrimeMicroMessages(index)
      );

      setCurrentGalleryIndex(index);
    }
  };

  return (
    <ErrorBoundary
      label="hotel.information.details.photoGallery"
      fallback={<UitkQueuedImage src={propertyGallery[0].image.url} useBackgroundImage={true} />}
    >
      <UitkLayoutFlexItem grow={1}>
        <IntersectionTracker
          tag="span"
          referrerId={`${RfrrPageNamespace.Search}.view.impression.${referrerId}`}
          linkName="Listing carousel view impression."
          additionalUisPrimeMicroMessages={uisPrimeMicroMessages(currentGalleryIndex)}
        >
          <UitkGalleryCarousel
            allyHeadingLevel={4}
            allyHeadingText={l10n.formatText(`hotels.infosite.gallery.a11yLabel`)}
            carouselName={`property-listing-${hotelId}-image-carousel${sponsoredListing ? '-sponsored' : ''}`}
            currentIndex={currentGalleryIndex}
            enablePreload={false}
            images={getGalleryModel(propertyGallery).images}
            navTheme={UitkButtonTheme.TRANSPARENT}
            onImageClick={navigateToDestination}
            onIndexChange={updateIndex}
            ratio={ratio}
            showGalleryButton={false}
            showScrim={true}
          />
        </IntersectionTracker>
      </UitkLayoutFlexItem>
      {shortList?.metaData.hotelId && (
        <FavoriteButton
          category="hotel"
          id={createShortlistId(hotelId, mapListingView, sponsoredListing !== null)}
          labelRemove={l10n.formatText('hotels.searchResults.shortlist.removeFromList.a11yLabel', [name])}
          labelSave={l10n.formatText('hotels.searchResults.shortlist.saveToList.a11yLabel', [name])}
          onClick={onFavoriteClick}
          onSaveMessage={l10n.formatText('hotels.shortlist.saveToList.notification', [name])}
          shortlistModel={adaptShortList(shortList)}
        />
      )}
    </ErrorBoundary>
  );
};

export function createShortlistId(hotelId: number, mapListingView: boolean, isSponsored?: boolean): string {
  const shortlistId = `listing-gallery-favorite-${hotelId}`;

  if (mapListingView) {
    return `map-card-favorite-${hotelId}`;
  }

  if (isSponsored) {
    return `sponsored-favorite-${hotelId}`;
  }

  return shortlistId;
}
